import styled from '@emotion/native';
import { useTheme } from '@emotion/react';
import type { UserPreferenceWard } from '@quromedical/models';
import { Col, Row } from 'components/base';
import { Button, type IconName, Text, Visible } from 'design-system/base';
import {
  BASE_SIZE,
  BORDER_RADIUS,
  BORDER_WIDTH,
  MARGIN,
} from 'design-system/theme';
import { getInitials } from 'helpers/name';
import { useIsSmallerThan } from 'hooks/useResponsive';
import React, { useCallback } from 'react';
import { FlatList, type ListRenderItem, View } from 'react-native';

export interface ProfileProps {
  name?: string;
  wards: UserPreferenceWard[];
  ward?: string;
  onWardSelect: (id: string) => void;
  onProfilePress?: () => void;
}

const Pressable = styled.Pressable({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
});

const InitialWrapper = styled(Col)(({ theme }) => ({
  alignItems: 'center',
  justifyContent: 'center',
  height: BASE_SIZE[36],
  width: BASE_SIZE[36],
  backgroundColor: theme.color.base,
  borderColor: theme.color.divider,
  borderStyle: 'solid',
  borderRadius: BORDER_RADIUS.full,
  borderWidth: BORDER_WIDTH[2],
}));

const TextWrapper = styled(Col)({
  marginRight: MARGIN.xs,
});

const Wrapper = styled(Col)(() => ({
  flex: 1,
  marginTop: MARGIN['3xs'],
  marginHorizontal: MARGIN.s,
}));

type OnSelect = (id: string) => void;
type WardComponentProps = UserPreferenceWard & {
  onSelect: OnSelect;
};
export const WardComponent: React.FC<WardComponentProps> = ({
  name,
  onSelect,
  id,
}) => (
  <Row unsetZIndex flex={1} flexWrap="wrap">
    <Button
      textPosition="flex-start"
      size="full-width"
      variant="text"
      onPress={() => onSelect(id)}
      text={name}
    />
  </Row>
);

export const WardSelect: React.FC<{
  wards: UserPreferenceWard[];
  selected?: string;
  onSelect: OnSelect;
}> = ({ onSelect, selected, wards }) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = React.useState(false);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);
  const icon: IconName = isOpen ? 'expand-less' : 'expand-more';

  const onItemSelect = useCallback(
    (id: string) => {
      onSelect(id);
      toggle();
    },
    [toggle, onSelect],
  );

  const ward = wards.find(({ id }) => id === selected)?.name || 'Select Ward';
  const renderItem: ListRenderItem<UserPreferenceWard> = useCallback(
    ({ item }) => {
      return <WardComponent {...item} onSelect={onItemSelect} />;
    },
    [onItemSelect],
  );

  if (!wards.length) {
    return null;
  }

  return (
    <Wrapper unsetZIndex>
      <View
        style={{
          backgroundColor: isOpen ? theme.color.base : undefined,
          width: '100%',
        }}
      >
        <Button
          size="full-width"
          iconPosition="right"
          icon={icon}
          text={ward}
          variant="text"
          onPress={toggle}
        />
      </View>
      <Visible if={isOpen}>
        <View style={{ zIndex: 1, backgroundColor: theme.color.base }}>
          <FlatList
            renderItem={renderItem}
            data={wards}
            keyExtractor={(i) => i.id}
          />
        </View>
      </Visible>
    </Wrapper>
  );
};

/**
 * Note that due to the way overflows and absolute positioning work on native it may be necessary to
 * create a native version of this component that uses relative positioning instead
 */
export const Profile: React.FC<ProfileProps> = ({
  name = '',
  onProfilePress,
  wards,
  ward,
  onWardSelect,
}) => {
  const isSmall = useIsSmallerThan('sm');

  const initials = getInitials(name);

  return (
    <Row unsetZIndex flex={1} alignItems="center" justifyContent="flex-end">
      <WardSelect onSelect={onWardSelect} wards={wards} selected={ward} />
      <Pressable onPress={onProfilePress}>
        <TextWrapper isVisible={!isSmall}>
          <Text selectable={false}>{name}</Text>
        </TextWrapper>
        <InitialWrapper isVisible={!!initials}>
          <Text selectable={false}>{initials}</Text>
        </InitialWrapper>
      </Pressable>
    </Row>
  );
};
